import { useEffect, useRef, useState } from "react";
import "./VEditor.css";
import { VTextType } from "./entitiy/VText";
import { WSMedia } from "../../../services/remote/media/WSMedia";

interface VEditorProps {
  value: string;
  onChange: (value: string) => void;
}

export default function VEditor({ value, onChange }: VEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const mediaInstance = WSMedia.getInstance();
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const savedRangeRef = useRef<Range | null>(null);

  useEffect(() => {
    const fetchUrl = async () => {
      try {
        const url = await mediaInstance.getGeneratedURL();
        setGeneratedUrl(url);
      } catch (error) {
        console.error("Failed to get generated URL:", error);
      }
    };

    fetchUrl();
  }, []);

  // Track the last selection made inside the editor so it can be restored
  // later (e.g. after the native file picker steals focus).
  useEffect(() => {
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      if (editorRef.current?.contains(range.commonAncestorContainer)) {
        savedRangeRef.current = range.cloneRange();
      }
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () =>
      document.removeEventListener("selectionchange", handleSelectionChange);
  }, []);

  const syncValue = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const clearAll = () => {
    if (editorRef.current) {
      editorRef.current.innerHTML = "";
      syncValue();
    }
  };

  const applyFormatting = (type: VTextType) => {
    editorRef.current?.focus();

    switch (type) {
      case VTextType.BOLD:
        document.execCommand("bold");
        break;
      case VTextType.ITALIC:
        document.execCommand("italic");
        break;
      case VTextType.UNDERLINE:
        document.execCommand("underline");
        break;
      case VTextType.H1:
        document.execCommand("formatBlock", false, "h1");
        break;
      case VTextType.H2:
        document.execCommand("formatBlock", false, "h2");
        break;
      case VTextType.H3:
        document.execCommand("formatBlock", false, "h3");
        break;
    }

    syncValue();
  };

  const handleHeadingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    switch (e.target.value) {
      case "h1":
        applyFormatting(VTextType.H1);
        break;
      case "h2":
        applyFormatting(VTextType.H2);
        break;
      case "h3":
        applyFormatting(VTextType.H3);
        break;
    }

    e.target.value = "";
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();

    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");

    editorRef.current?.focus();

    if (html) {
      document.execCommand("insertHTML", false, html);
    } else {
      document.execCommand("insertText", false, text);
    }

    requestAnimationFrame(syncValue);
  };

  // Generic helper: insert a DOM node at the last known cursor position,
  // falling back to the end of the editor if no selection was ever made.
  const insertNodeAtCursor = (node: HTMLElement) => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();

    const spacer = document.createTextNode("\u00A0");
    const selection = window.getSelection();
    const range =
      savedRangeRef.current &&
      editor.contains(savedRangeRef.current.startContainer)
        ? savedRangeRef.current
        : null;

    if (selection && range) {
      selection.removeAllRanges();
      selection.addRange(range);

      range.deleteContents();
      range.insertNode(spacer);
      range.insertNode(node);

      range.setStartAfter(spacer);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      editor.appendChild(node);
      editor.appendChild(spacer);
    }

    savedRangeRef.current = null;
  };

  // Removable image "chip": the <img> plus a small × delete button,
  // wrapped as a single non-editable inline unit.
  const buildImageWrapper = (imageUrl: string) => {
    const wrapper = document.createElement("span");
    wrapper.className = "veditor-image-wrap";
    wrapper.contentEditable = "false";

    const img = document.createElement("img");
    img.src = imageUrl;
    img.alt = "Uploaded image";
    img.className = "veditor-image";

    const removeBtn = document.createElement("button");
    removeBtn.type = "button";
    removeBtn.className = "veditor-image-remove";
    removeBtn.setAttribute("aria-label", "Remove image");
    removeBtn.innerHTML = "&times;";

    wrapper.appendChild(img);
    wrapper.appendChild(removeBtn);

    return wrapper;
  };

  // Placeholder chip shown at the cursor while the image is uploading.
  const buildLoadingPlaceholder = (uploadId: string) => {
    const wrapper = document.createElement("span");
    wrapper.className = "veditor-image-wrap veditor-image-loading";
    wrapper.contentEditable = "false";
    wrapper.setAttribute("data-upload-id", uploadId);

    const spinner = document.createElement("span");
    spinner.className = "veditor-spinner";

    wrapper.appendChild(spinner);
    return wrapper;
  };

  const insertImageAtCursor = (imageUrl: string) => {
    insertNodeAtCursor(buildImageWrapper(imageUrl));
    syncValue();
  };

  // Upload local file -> save to S3 -> insert image inline at the cursor.
  // Shows a spinner placeholder at the cursor while the upload is running.
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !generatedUrl || isUploadingImage) return;

    const uploadId = `upload-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const placeholder = buildLoadingPlaceholder(uploadId);

    setIsUploadingImage(true);
    insertNodeAtCursor(placeholder);
    syncValue();

    try {
      const imageUrl = await mediaInstance.saveImage(generatedUrl, file);
      const finished = editorRef.current?.querySelector(
        `[data-upload-id="${uploadId}"]`,
      );
      finished?.replaceWith(buildImageWrapper(imageUrl));
    } catch (error) {
      console.error("Image upload failed:", error);
      editorRef.current
        ?.querySelector(`[data-upload-id="${uploadId}"]`)
        ?.remove();
    } finally {
      setIsUploadingImage(false);
      syncValue();
      e.target.value = ""; // reset input
    }
  };

  // Save the current selection right before the native file picker opens,
  // since opening it steals focus and would otherwise lose the cursor spot.
  const handleImageButtonMouseDown = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (editorRef.current?.contains(range.commonAncestorContainer)) {
        savedRangeRef.current = range.cloneRange();
      }
    }
  };

  // Delegated click handler: catches clicks on any image's × button.
  const handleEditorClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const removeBtn = target.closest(".veditor-image-remove");
    if (!removeBtn) return;

    removeBtn.closest(".veditor-image-wrap")?.remove();
    syncValue();
  };

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  return (
    <div className="veditor-container">
      <div className="veditor-header">
        <h1 className="veditor-title">V-Editor</h1>

        <div className="veditor-toolbar">
          <select
            onChange={handleHeadingChange}
            className="veditor-heading-select"
            defaultValue=""
          >
            <option value="">Heading</option>
            <option value="h1">H1</option>
            <option value="h2">H2</option>
            <option value="h3">H3</option>
          </select>

          <div className="veditor-toolbar-divider" />

          <button
            type="button"
            onClick={() => applyFormatting(VTextType.BOLD)}
            className="veditor-btn"
            aria-label="Bold"
            title="Bold"
          >
            <strong>B</strong>
          </button>

          <button
            type="button"
            onClick={() => applyFormatting(VTextType.ITALIC)}
            className="veditor-btn"
            aria-label="Italic"
            title="Italic"
          >
            <em>I</em>
          </button>

          <button
            type="button"
            onClick={() => applyFormatting(VTextType.UNDERLINE)}
            className="veditor-btn"
            aria-label="Underline"
            title="Underline"
          >
            <u>U</u>
          </button>

          <div className="veditor-toolbar-divider" />

          {/* Image upload button */}
          <label
            className={`veditor-btn veditor-btn-image${
              isUploadingImage ? " veditor-btn-disabled" : ""
            }`}
            onMouseDown={handleImageButtonMouseDown}
            title="Insert image"
          >
            {isUploadingImage ? (
              <span className="veditor-spinner veditor-spinner-sm" />
            ) : (
              "🖼️"
            )}
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleImageUpload}
              disabled={isUploadingImage}
            />
          </label>

          <button
            type="button"
            onClick={clearAll}
            className="veditor-btn veditor-btn-danger"
            aria-label="Clear"
            title="Clear all"
          >
            ✕
          </button>
        </div>
      </div>

      <div
        ref={editorRef}
        className="veditor"
        contentEditable
        suppressContentEditableWarning
        onInput={syncValue}
        onPaste={handlePaste}
        onClick={handleEditorClick}
      />
    </div>
  );
}
